import { createFileRoute, Link } from '@tanstack/react-router';
import { LegalDocument, LegalSection } from '@/components/legal/legal-document';

export const Route = createFileRoute('/privacy-policy')({
  component: PrivacyPolicyPage,
});

const EFFECTIVE_DATE = 'November 1, 2026';

function PrivacyPolicyPage() {
  return (
    <LegalDocument
      title="Privacy Policy | Boundr"
      description="How Boundr (Yashna Labs LLC) collects, uses, shares, and deletes personal data for land monitoring customers worldwide."
      path="/privacy-policy"
      heading="Privacy Policy"
      effectiveDate={EFFECTIVE_DATE}
    >
      <LegalSection id="overview" title="1. Overview">
        <p>
          This Privacy Policy explains how Yashna Labs LLC (&quot;Boundr,&quot; &quot;we,&quot;
          &quot;us,&quot; or &quot;our&quot;) handles personal information when you use Boundr&apos;s
          land monitoring / plot inspection service and related websites, dashboards, and
          communications (the &quot;Service&quot;). We operate worldwide from Washington, USA.
        </p>
        <p>
          By creating an account or using the Service, you agree to this Policy. If you do not
          agree, do not use the Service.
        </p>
      </LegalSection>

      <LegalSection id="data-we-collect" title="2. Personal data we collect">
        <p>We collect only what we need to provide land monitoring:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <span className="text-foreground">Account &amp; contact details</span> — name, email
            address, and phone number (including numbers you share so we can reach you on WhatsApp
            or similar messaging apps).
          </li>
          <li>
            <span className="text-foreground">Property details</span> — plot location and related
            information you submit so a visit can be performed.
          </li>
          <li>
            <span className="text-foreground">Payment information</span> — billing country and
            payment metadata processed by our payment gateway provider(s). We do not store full
            card or UPI credentials on our servers.
          </li>
          <li>
            <span className="text-foreground">Inspection media &amp; reports</span> — photos,
            video, checklist answers, GPS/timestamp metadata, and the resulting digital report.
          </li>
          <li>
            <span className="text-foreground">Usage &amp; device data</span> — cookies, device and
            browser signals, approximate location derived from IP, fingerprinting identifiers,
            session replay recordings, and product analytics events (via PostHog and similar
            tools).
          </li>
          <li>
            <span className="text-foreground">Support messages</span> — content you send through
            our{' '}
            <Link to="/contact-us" className="text-ring underline-offset-4 hover:underline">
              Contact Us
            </Link>{' '}
            form.
          </li>
        </ul>
        <p>We do not intentionally collect government ID numbers (such as Aadhaar or passport).</p>
      </LegalSection>

      <LegalSection id="how-we-use" title="3. How we use personal data">
        <ul className="list-disc space-y-2 pl-5">
          <li>Create and manage your account and subscription</li>
          <li>Schedule and deliver plot inspections and digital reports</li>
          <li>Process payments and prevent fraud</li>
          <li>Communicate about visits, reports, billing, and service changes</li>
          <li>Operate, secure, debug, and improve the Service (including QA and training)</li>
          <li>Analyze product usage (including session replay) and measure marketing performance</li>
          <li>Comply with law and enforce our Terms of Use</li>
        </ul>
        <p>
          Marketing and product communications are bundled with your account relationship. You may
          ask us to stop non-essential marketing messages via the Contact Us form; transactional
          messages about your subscription and reports may continue while your account is active.
        </p>
      </LegalSection>

      <LegalSection id="sharing" title="4. How we share personal data">
        <p>We do not sell personal data.</p>
        <p>
          We share personal data only as needed to operate the Service, including with:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <span className="text-foreground">Service partners</span> — Boundr may engage
            subcontractors and an on-ground service partner to perform visits. We share only what
            is needed for a visit (for example, plot location and access notes). Field personnel do
            not communicate directly with customers through Boundr.
          </li>
          <li>
            <span className="text-foreground">Infrastructure &amp; product vendors</span> — AWS
            (hosting/storage in <code className="text-foreground">us-west-2</code>), WorkOS
            (authentication), PostHog (analytics, cookies, fingerprinting, session replay), Sentry
            (error monitoring), Google Maps (maps/geocoding), and our payment gateway provider(s).
          </li>
          <li>
            <span className="text-foreground">Legal &amp; safety</span> — when required by law, or
            to protect Boundr, our users, or others.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="international" title="5. International transfers">
        <p>
          Primary storage is in AWS region <code className="text-foreground">us-west-2</code>{' '}
          (United States). Because we serve customers worldwide and use on-ground service partners
          outside the United States, personal data may be accessed from or transferred to other
          countries, including India, with appropriate contractual and technical safeguards.
        </p>
      </LegalSection>

      <LegalSection id="retention" title="6. Retention and deletion">
        <p>
          When your subscription ends or payment stops, Boundr purges account content and
          inspection media promptly. There is no service grace period after payment stops.
        </p>
        <p>
          Limited payment or transaction records may be retained only as required by law or by our
          payment gateway provider(s). Support tickets submitted via Contact Us may be retained as
          needed to resolve your request and for legitimate business records.
        </p>
        <p>
          Account deletion is not fully self-serve yet. Request deletion or a copy of your data
          through our{' '}
          <Link to="/contact-us" className="text-ring underline-offset-4 hover:underline">
            Contact Us
          </Link>{' '}
          form. We will respond within a reasonable time.
        </p>
      </LegalSection>

      <LegalSection id="cookies" title="7. Cookies, fingerprinting, and session replay">
        <p>
          We and our analytics provider (PostHog) use cookies, local storage, device fingerprinting,
          session replay, and similar technologies to operate the site, keep you signed in, measure
          performance, and understand how the Service is used. You can control cookies through your
          browser settings; disabling some cookies may limit functionality.
        </p>
      </LegalSection>

      <LegalSection id="rights" title="8. Your privacy rights">
        <p>
          Depending on where you live, you may have rights to access, correct, delete, or restrict
          use of your personal data, to withdraw consent where processing is consent-based, and to
          lodge a complaint with a supervisory authority.
        </p>
        <p>
          <span className="text-foreground">India (DPDP Act, 2023).</span> If you are a Data
          Principal in India, you may request access, correction, erasure, and grievance redressal
          regarding personal data we process about you, subject to applicable exceptions.
        </p>
        <p>
          <span className="text-foreground">European Economic Area / UK (GDPR / UK GDPR).</span>{' '}
          Where these laws apply, we process personal data to perform our contract with you, for
          our legitimate interests in operating and securing the Service, and where required by
          law. You may have rights of access, rectification, erasure, restriction, portability, and
          objection.
        </p>
        <p>
          <span className="text-foreground">California / similar US state laws (CCPA/CPRA).</span>{' '}
          We do not sell or share personal information for cross-context behavioral advertising as
          those terms are commonly defined. You may request to know, delete, or correct personal
          information, subject to exceptions.
        </p>
        <p>
          To exercise any privacy right, use the{' '}
          <Link to="/contact-us" className="text-ring underline-offset-4 hover:underline">
            Contact Us
          </Link>{' '}
          form. We may need to verify your identity before fulfilling a request.
        </p>
      </LegalSection>

      <LegalSection id="children" title="9. Children">
        <p>
          The Service is for adults only (18+). By signing up, you represent that you meet this
          requirement. We do not knowingly collect personal data from children. If you believe a
          child has provided data, contact us and we will delete it.
        </p>
      </LegalSection>

      <LegalSection id="security" title="10. Security">
        <p>
          We use administrative, technical, and organizational measures appropriate to the nature
          of the data we process. No method of transmission or storage is completely secure; use of
          the Service is at your own risk.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="11. Changes to this Policy">
        <p>
          We may update this Privacy Policy from time to time. The effective date above will
          change when we do. Continued use of the Service after an update means you accept the
          revised Policy.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="12. Contact">
        <p>
          Privacy and data requests are accepted only through our{' '}
          <Link to="/contact-us" className="text-ring underline-offset-4 hover:underline">
            Contact Us
          </Link>{' '}
          form. Boundr / Yashna Labs LLC, Washington, USA.
        </p>
      </LegalSection>
    </LegalDocument>
  );
}
